export const locales = ['en', 'es'] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = 'es';

/**
 * Normalize various locale formats/synonyms into supported app locales
 *
 * @param input - The locale string to normalize (can be BCP-47, language name, etc.)
 * @returns A normalized Locale ('en' or 'es')
 *
 * @example
 * ```typescript
 * normalizeLocale('en-US') // 'en'
 * normalizeLocale('español') // 'es'
 * normalizeLocale('eng') // 'en'
 * normalizeLocale(null) // 'es' (default)
 * ```
 */
export function normalizeLocale(input?: string | null): Locale {
  if (!input) return defaultLocale;
  const value = String(input).toLowerCase();

  // English variants
  if (
    value === 'en' ||
    value === 'en-us' ||
    value === 'en_gb' ||
    value === 'en-uk' ||
    value === 'english' ||
    value === 'eng' ||
    value === 'en-us;q=1'
  ) {
    return 'en';
  }

  // Spanish variants
  if (
    value === 'es' ||
    value === 'es-es' ||
    value === 'es_mx' ||
    value === 'es-419' ||
    value === 'spanish' ||
    value === 'español' ||
    value === 'esp'
  ) {
    return 'es';
  }

  // Try to coerce from BCP-47 like "en-XX"
  const two = value.slice(0, 2);
  if (two === 'en' || two === 'es') return two as Locale;

  return defaultLocale;
}

/**
 * Map our app locales to the backend's expected language values
 *
 * @param input - The locale string to convert
 * @returns The backend language enum value ('ENGLISH' or 'SPANISH')
 *
 * @example
 * ```typescript
 * toBackendLanguage('en') // 'ENGLISH'
 * toBackendLanguage('es') // 'SPANISH'
 * toBackendLanguage(null) // undefined
 * ```
 */
export function toBackendLanguage(input?: string | null): string | undefined {
  if (!input) return undefined;
  const loc = normalizeLocale(input);
  return loc === 'en' ? 'ENGLISH' : 'SPANISH';
}
